export default String.raw`
/**
 * @module Comparer
 * @description This module provides functionality to compare products. It includes methods to add and remove products from the comparer. It also includes events for adding, removing and clearing the comparer.
 * @exports {function} add - Adds a product to the comparer.
 * @exports {function} remove - Removes a product from the comparer.
 * @exports {function} clear - Clears the comparer.
 * @exports {event} comparer:added - The event is fired when a product is added to the comparer.
 * @exports {event} comparer:removed - The event is fired when a product is removed from the comparer.
 * @exports {event} comparer:empty - The event is fired when the comparer is cleared.
 * @exports {event} comparer:full - The event is fired when the comparer is full.
 */
const Comparer = (() => {
    'use strict';
    const maxItems = parseInt(__productComparer) || 4;
    let _itemsCount = parseInt(__productComparerSize) || 0;
    localStorage.setItem('comparer-itemsCount', _itemsCount);

    const itemsCount = {
        get value() {
            return _itemsCount;
        },
        set value(value) {
            if (typeof value === 'number' && value >= 0) {
                _itemsCount = value;
                localStorage.setItem('comparer-itemsCount', value);
                window.dispatchEvent(new CustomEvent('comparer:itemsCountChanged', { 
                    detail: { 
                        itemsCount: value
                    } 
                }));

                if (_itemsCount === 0) {
                    window.dispatchEvent(new CustomEvent('comparer:empty'));
                }
            } else {
                console.warn('Invalid value for itemsCount. It must be a non-negative number.');
            }
        }
    };

    window.addEventListener('storage', (e) => {
        if (e.key === 'comparer-itemsCount') {
            itemsCount.value = parseInt(e.newValue) || 0;
        }
    });

    /**
     * @event {Event} comparer:added
     * @description The event is fired when a product is added to the comparer.
    */

    /**
     * @event {Event} comparer:removed
     * @description The event is fired when a product is removed to the comparer.
    */

    /**
     * @event {Event} comparer:empty
     * @description The event is fired when a product is cleared.
    */

    /**
     * @event {Event} comparer:full
     * @description The event is fired when a product is full.
    */

    /**
     * Add a product to the comparer.
     * @public
     * @method add
     * @param {number} productId - The product ID to be added.
     * @fires comparer:added
     * @fires comparer:full
     * @returns {void}
     * 
     * @example
     * Comparer.add(123);
    */
    const add = async (productId) => {
        if (itemsCount.value >= maxItems) {
            window.dispatchEvent(new CustomEvent('comparer:full'));
            return;
        }

        const data = {
            __csrf: __CSRF, 
            __action: 'Product/ComparisonToolAdd',
            __collection: 'customer.ComparisonToolItems',
            productId: productId
        };

        try {
            const response = await $.post('', data);
            itemsCount.value = response?.collection?.length;

            window.dispatchEvent(new CustomEvent('comparer:added', { 
                detail: { 
                    productId,
                    itemsCount: itemsCount.value
                } 
            }));
        } catch (error) {
            console.error('Failed to add item to the comparer.', error);
        }
    };

    /**
     * Removes a product from the comparer.
     * @public
     * @method remove
     * @param {number} productId - The product ID to be removed.
     * @fires comparer:removed
     * @fires comparer:empty
     * @returns {void}
     * 
     * @example
     * Comparer.remove(123);
    */
    const remove = async (productId) => {
        if (itemsCount.value <= 0) {
            window.dispatchEvent(new CustomEvent('comparer:empty'));
            return;
        }
    
        const data = {
            __csrf: __CSRF,
            __action: 'Product/ComparisonToolDelete',
            __collection: 'customer.ComparisonToolItems',
            productId: productId
        };
    
        try {
            const response = await $.post('', data);
            itemsCount.value = response?.collection?.length || 0;
            
            window.dispatchEvent(new CustomEvent('comparer:removed', { 
                detail: { 
                    productId,
                    itemsCount: itemsCount.value
                } 
            }));

            if (itemsCount.value === 0) {
                window.dispatchEvent(new CustomEvent('comparer:empty'));
            }
        } catch (error) {
            console.error('Failed to remove item from the comparer.', error);
        }
    };

    /**
     * Clears the comparer.
     * @public
     * @method clear
     * @param {Array<number>} productsIds - The products IDs to be removed.
     * @returns {void}
     * 
     * @example
     * Comparer.clear([123, 321, 134, 434]);
    */
    const clear = async (productsIds) => {
        productsIds.forEach(productId => {
            // There is no serial removing from comparison action ...
            remove(productId);
        });
    };

    return {
        add,
        remove,
        clear,
        itemsCount
    };
})();

/**
 * @module ComparerUI
 * @description 
 * @requires module:Comparer
 */
const ComparerUI = (() => {
    'use strict';
    $('body').on('click', '.add-to-comparison-js', (e) => {
        const productId = $(e.currentTarget).data('product-id');
        Comparer.add(productId);
    });

    $('body').on('click', '.remove-from-comparison-js', (e) => {
        const productId = $(e.currentTarget).data('product-id');
        Comparer.remove(productId);
    });

    $('body').on('click', '.clear-comparer-js', async () => {
        const data = {
            __csrf: __CSRF,
            __collection: 'customer.ComparisonToolItems'
        }

        try {
            const response = await $.get('', data);
            const productsIds = response?.collection;

            if (!productsIds) {
                return;
            }

            Comparer.clear(productsIds);

        } catch (error) {
            const inputs = document.querySelectorAll('.clear-comparer-form-js input[name="productId"]');

            if (!inputs) {
                return;
            }
            
            const productsIds = Array.from(inputs).map(input => input.value);
            Comparer.clear(productsIds);
        }
    });

    const comparison = document.querySelector('.comparer-js');

    if (!comparison) {
        return;
    }

    window.addEventListener('comparer:removed', () => {
        setTimeout(() => {
            location.reload();
        }, 1000);
    });

    let sliderInitialized = false;
    const elements = {
        mainSlider: '.comparer-main-slider-js',
        slider: '.comparer-slider-js',
        bar: document.querySelector('.comparer-header-js'),
        threshold: document.querySelector('.comparer-treshold-js'),
        gradient: document.querySelectorAll('.gradient-js')
    };

    /**
     * @private
     * @method toggleGradientVisibility
     * @returns
    */
    const toggleGradientVisibility = (visible) => {
        let visibility;
        if (visible) {
            visibility = "visible";
        } else {
            visibility = "hidden";
        }
        elements.gradient.forEach(element => {
            element.style.visibility = visibility;
        });
    };

    const initSlider = () => {
        if (sliderInitialized) {
            return;
        }
        
        const mainSlider = new Swiper(elements.mainSlider, {
            slidesPerView: 'auto',
            allowTouchMove: true,
            breakpoints: {
                1439: {
                    allowTouchMove: false
                }
            },
            navigation: {
                nextEl: '.comparer__slider-button-next',
                prevEl: '.comparer__slider-button-prev',
            },
            on: {
                reachEnd: () => toggleGradientVisibility(false),
                slidePrevTransitionStart: () => toggleGradientVisibility(true),
            },
        });

        const sliders = new Swiper(elements.slider, {
            slidesPerView: 'auto',
            allowTouchMove: true,
            controller: {
                control: mainSlider,
            },
            breakpoints: {
                1439: {
                    allowTouchMove: false
                }
            },
        });
        mainSlider.controller.control = sliders;
        sliderInitialized = true;
    };

    const initStickyHeader = () => {
        const intersectionObserver = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (entry.isIntersecting) {
                elements.bar.style.opacity = '0';
                elements.bar.style.visibility = 'hidden';
            } else {
                elements.bar.style.opacity = '1';
                elements.bar.style.visibility = 'visible';
            }
        }, { threshold: 0.25});

        intersectionObserver.observe(elements.threshold);

        const header = document.querySelector('header');
        if (!header) {
            return;
        }
        const mutationObserver = new MutationObserver(function() {
            if (header.classList.contains('fixed')) {
                const headerHeight = header.offsetHeight;
                elements.bar.style.top = headerHeight + 'px';
            } else {
                elements.bar.style.top = '0px';
            }
        });

        mutationObserver.observe(header, { attributes: true, childList: true, subtree: true });

        if (header.classList.contains('fixed')) {
            elements.bar.style.top =  header.offsetHeight + 'px';
        } else {
            elements.bar.style.top = '0px';
        }    
    };

    if(elements.mainSlider && elements.slider) {
        initSlider();
    }

    if (elements.bar && elements.threshold) {
        initStickyHeader();
    }
})();

`